import React, { useState } from "react";
import { Download, Printer, BarChart3, Loader2 } from "lucide-react";
import api, { money } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const GROUPS = [
  { key: "category", label: "Categoría / Concepto" },
  { key: "responsible", label: "Responsable" },
  { key: "cost_center", label: "Centro de costo" },
  { key: "project", label: "Proyecto" },
  { key: "document", label: "Documento" },
];
const iso = (d) => d.toISOString().slice(0, 10);

export default function CajaReportes() {
  const now = new Date(Date.now()-216e5);
  const [df, setDf] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [dt, setDt] = useState(iso(now));
  const [groupBy, setGroupBy] = useState("category");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const preset = (kind) => {
    const t = new Date(); let from = new Date();
    if (kind === "day") from = t;
    if (kind === "week") from = new Date(t.getTime() - 6 * 864e5);
    if (kind === "month") from = new Date(t.getFullYear(), t.getMonth(), 1);
    if (kind === "year") from = new Date(t.getFullYear(), 0, 1);
    setDf(iso(from)); setDt(iso(t));
  };

  const gen = async () => {
    setLoading(true);
    try { const r = await api.get("/pettycash/report", { params: { date_from: df, date_to: dt, group_by: groupBy } }); setData(r.data); }
    catch { toast.error("No se pudo generar el reporte"); } finally { setLoading(false); }
  };

  const groupLabel = GROUPS.find((g) => g.key === groupBy)?.label || groupBy;

  const exportCSV = () => {
    if (!data) return;
    const head = [groupLabel, "Ingreso", "Egreso", "Neto", "Movimientos"];
    const lines = [head.join(",")];
    data.groups.forEach((g) => lines.push([`"${g.key}"`, g.ingreso, g.egreso, g.neto, g.count].join(",")));
    lines.push(["TOTAL", data.totals.ingreso, data.totals.egreso, data.totals.neto, data.totals.count].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `caja_chica_${df}_${dt}.csv`; a.click();
  };

  const printReport = () => {
    if (!data) return;
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const rowsHtml = data.groups.map((g) => `<tr><td>${esc(g.key)}</td><td style="text-align:right">Q ${g.ingreso.toFixed(2)}</td><td style="text-align:right">Q ${g.egreso.toFixed(2)}</td><td style="text-align:right">Q ${g.neto.toFixed(2)}</td><td style="text-align:center">${g.count}</td></tr>`).join("");
    const html = `<html><head><title>Reporte Caja Chica</title><style>body{font-family:Arial;padding:24px;color:#222}h2{color:#F26A00}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #ddd;padding:6px 8px;font-size:12px}th{background:#F26A00;color:#fff;text-align:left}tfoot td{font-weight:bold;background:#f5f5f5}</style></head><body><h2>SELEC · Reporte de Caja Chica</h2><p>Período: ${esc(df)} a ${esc(dt)} · Agrupado por: ${esc(groupLabel)}</p><table><thead><tr><th>${esc(groupLabel)}</th><th>Ingreso</th><th>Egreso</th><th>Neto</th><th>Mov.</th></tr></thead><tbody>${rowsHtml}</tbody><tfoot><tr><td>TOTAL</td><td style="text-align:right">Q ${data.totals.ingreso.toFixed(2)}</td><td style="text-align:right">Q ${data.totals.egreso.toFixed(2)}</td><td style="text-align:right">Q ${data.totals.neto.toFixed(2)}</td><td style="text-align:center">${data.totals.count}</td></tr></tfoot></table></body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    w.location.href = url;
    setTimeout(() => { w.focus(); w.print(); URL.revokeObjectURL(url); }, 400);
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-sm border shadow-none p-3 space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => preset("day")} data-testid="preset-day">Hoy</Button>
          <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => preset("week")} data-testid="preset-week">Semana</Button>
          <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => preset("month")} data-testid="preset-month">Mes</Button>
          <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => preset("year")} data-testid="preset-year">Año</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
          <div className="space-y-1"><Label className="text-[10px] uppercase tracking-widest font-bold">Desde</Label><Input type="date" className="rounded-sm h-9 text-xs" value={df} onChange={(e) => setDf(e.target.value)} data-testid="report-from" /></div>
          <div className="space-y-1"><Label className="text-[10px] uppercase tracking-widest font-bold">Hasta</Label><Input type="date" className="rounded-sm h-9 text-xs" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="report-to" /></div>
          <div className="space-y-1"><Label className="text-[10px] uppercase tracking-widest font-bold">Agrupar por</Label>
            <Select value={groupBy} onValueChange={setGroupBy}><SelectTrigger className="rounded-sm h-9 text-xs" data-testid="report-groupby"><SelectValue /></SelectTrigger><SelectContent>{GROUPS.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}</SelectContent></Select></div>
          <Button className="rounded-sm gap-2 font-bold text-xs" onClick={gen} disabled={loading} data-testid="generate-report-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />} Generar</Button>
        </div>
      </Card>

      {data && (<>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" size="sm" className="rounded-sm gap-2 text-xs" onClick={exportCSV} data-testid="export-csv-btn"><Download className="h-4 w-4" /> Exportar CSV</Button>
          <Button variant="outline" size="sm" className="rounded-sm gap-2 text-xs" onClick={printReport} data-testid="print-report-btn"><Printer className="h-4 w-4" /> Imprimir</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="rounded-sm p-3 border shadow-none"><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Ingresos</p><p className="text-lg font-bold font-mono text-emerald-600">{money("GTQ", data.totals.ingreso)}</p></Card>
          <Card className="rounded-sm p-3 border shadow-none"><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Egresos</p><p className="text-lg font-bold font-mono text-red-600">{money("GTQ", data.totals.egreso)}</p></Card>
          <Card className="rounded-sm p-3 border shadow-none"><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Neto</p><p className="text-lg font-bold font-mono text-primary">{money("GTQ", data.totals.neto)}</p></Card>
          <Card className="rounded-sm p-3 border shadow-none"><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Movimientos</p><p className="text-lg font-bold font-mono">{data.totals.count}</p></Card>
        </div>
        <Card className="rounded-sm border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-table">
              <thead><tr className="bg-muted text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2.5 font-bold">{groupLabel}</th><th className="px-3 py-2.5 font-bold text-right">Ingreso</th>
                <th className="px-3 py-2.5 font-bold text-right">Egreso</th><th className="px-3 py-2.5 font-bold text-right">Neto</th><th className="px-3 py-2.5 font-bold text-center">Mov.</th></tr></thead>
              <tbody>
                {data.groups.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">Sin datos en el período.</td></tr>}
                {data.groups.map((g, i) => (
                  <tr key={i} className="border-t border-border hover:bg-muted/40">
                    <td className="px-3 py-2">{g.key}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-600">{g.ingreso ? money("GTQ", g.ingreso) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-600">{g.egreso ? money("GTQ", g.egreso) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{money("GTQ", g.neto)}</td>
                    <td className="px-3 py-2 text-center">{g.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </>)}
    </div>
  );
}
